/* feels like mahjong, theme behaviour (v0.3). No framework, no dependencies.
   Every component works without JS; this layer adds the header states, the
   bag drawer, the product gallery and the product page helpers. */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const debounce = (fn, ms) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

/* Header: paper, always; hides while reading down and returns on the way up.
   It only changes after the page has travelled 12 px in one direction, so a
   trackpad's small reversals and momentum tails do not make it flicker. */
const HEADER_HIDE_AFTER = 320;
const HEADER_TRAVEL = 12;

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.section-header') || this;
    this.anchor = window.scrollY;
    this.hidden = false;
    this.queued = false;
    this.onScroll = this.onScroll.bind(this);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }
  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }
  onScroll() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => this.update());
  }
  update() {
    this.queued = false;
    const y = window.scrollY;
    const locked = document.body.classList.contains('is-locked');
    let hide = this.hidden;
    if (y < HEADER_HIDE_AFTER || locked) hide = false;
    else if (y - this.anchor > HEADER_TRAVEL) hide = true;
    else if (this.anchor - y > HEADER_TRAVEL) hide = false;
    // The anchor follows the page in the current direction, so travel is measured from the last turn.
    if ((hide && y > this.anchor) || (!hide && y < this.anchor) || hide !== this.hidden) this.anchor = y;
    if (hide !== this.hidden) {
      this.hidden = hide;
      this.wrapper.classList.toggle('is-hidden', hide);
    }
  }
}
customElements.define('site-header', SiteHeader);

/* Drawers and sheets share one pattern: open, trap focus, close on Escape
   or on the overlay, return focus to the trigger. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

class FlmPanel extends HTMLElement {
  connectedCallback() {
    this.overlay = document.getElementById(this.dataset.overlay);
    this.onKey = this.onKey.bind(this);
    this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.overlay?.addEventListener('click', () => this.close());
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) =>
      t.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(t);
      })
    );
  }
  open(trigger) {
    this.trigger = trigger;
    this.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.body.classList.add('is-locked');
    document.addEventListener('keydown', this.onKey);
    const first = this.querySelector('[data-autofocus]') || this.querySelector(FOCUSABLE);
    requestAnimationFrame(() => first?.focus());
  }
  close() {
    this.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('is-locked');
    document.removeEventListener('keydown', this.onKey);
    this.trigger?.focus();
  }
  onKey(e) {
    if (e.key === 'Escape') this.close();
    if (e.key !== 'Tab') return;
    const items = [...this.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
customElements.define('flm-panel', FlmPanel);

/* Bag: add forms post to /cart/add.js, then the drawer section is re-rendered
   through the Section Rendering API so its markup stays in Liquid. */
async function refreshCart() {
  const drawer = document.getElementById('CartDrawer');
  if (!drawer) return;
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
  if (!res.ok) throw new Error('The bag could not be refreshed. Reload the page to see it.');
  const html = new DOMParser().parseFromString(await res.text(), 'text/html');
  const fresh = html.querySelector('#CartDrawerInner');
  if (fresh) drawer.querySelector('#CartDrawerInner').replaceWith(fresh);
  const count = html.querySelector('[data-cart-count]')?.textContent;
  if (count != null) document.querySelectorAll('[data-cart-count]').forEach((el) => (el.textContent = count.trim()));
}

document.addEventListener('submit', async (e) => {
  const form = e.target.closest('form[data-add-to-cart]');
  if (!form) return;
  e.preventDefault();
  const buttons = [...document.querySelectorAll(`[type="submit"][form="${form.id}"]`), ...form.querySelectorAll('[type="submit"]')];
  const main = form.querySelector('[type="submit"]');
  // innerHTML, not text: the PDP button carries the price in its own span, which a variant change updates.
  const label = main?.innerHTML;
  const msg = form.querySelector('[data-cart-error]');
  if (msg) msg.textContent = '';
  buttons.forEach((b) => (b.disabled = true));
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.description || 'This could not be added to your bag. Please try again.');
    }
    if (main) main.textContent = main.dataset.added || 'Added';
    await refreshCart();
    document.getElementById('CartDrawer')?.open(main);
  } catch (err) {
    console.error('[add to bag]', err);
    if (msg) msg.textContent = err.message;
  } finally {
    buttons.forEach((b) => (b.disabled = false));
    if (main) setTimeout(() => {
      main.innerHTML = label;
      const picked = form.querySelector('[data-variant-select]')?.selectedOptions[0];
      const price = main.querySelector('[data-price]');
      if (picked && price) price.textContent = picked.dataset.price;
    }, 1600);
  }
});

/* Bag lines: the drawer's steppers and every Remove link post to
   /cart/change.js. In the drawer the bag is re-rendered in place and focus
   returns to the same control (or the heading when the line is gone); on the
   cart page the page reloads. A failure is shown under the lines. */
document.addEventListener('click', async (e) => {
  const control = e.target.closest('[data-line-qty], [data-line-remove]');
  if (!control) return;
  e.preventDefault();
  const drawer = control.closest('#CartDrawer');
  const line = Number(control.dataset.line);
  const now = Number(control.closest('[data-line-item]')?.dataset.qtyNow || 1);
  const quantity = control.hasAttribute('data-line-remove') ? 0 : Math.max(0, now + Number(control.dataset.lineQty));
  const msg = (drawer || document).querySelector('[data-cart-msg]');
  if (msg) msg.textContent = '';
  control.setAttribute('aria-disabled', 'true');
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.description || 'The bag could not be changed. Please try again.');
    }
    if (!drawer) { window.location.reload(); return; }
    const key = control.hasAttribute('data-line-remove') ? '[data-line-remove]' : `[data-line-qty="${control.dataset.lineQty}"]`;
    await refreshCart();
    const status = drawer.querySelector('[data-cart-status]');
    if (status) status.textContent = status.dataset.updated || 'Bag updated.';
    const again = quantity > 0 && drawer.querySelector(`[data-line="${line}"]${key}`);
    (again || drawer.querySelector('#CartDrawerTitle'))?.focus?.({ preventScroll: true });
  } catch (err) {
    console.error('[bag]', err);
    if (msg) msg.textContent = err.message;
  } finally {
    control.removeAttribute('aria-disabled');
  }
});

/* Product gallery. Wide screens: the photographs are a stack in the page;
   the rail and the counter follow whichever one is in view, and a thumbnail
   scrolls its photograph into place. Under 1000px: a swipe strip (native
   scroll snap) with a counter and a progress line. Any photograph opens the
   lightbox, which loads the largest file. The lightbox is a <dialog>, so the
   browser traps focus and Escape closes it; focus returns to the opener. */
class FlmGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.slides.length) return;
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.strip = this.querySelector('[data-slides]');
    this.indexEls = [...this.querySelectorAll('[data-index]')];
    this.progress = this.querySelector('[data-progress]');
    this.box = this.querySelector('[data-lightbox]');
    this.i = 0;
    this.narrow = window.matchMedia('(max-width: 999px)');

    this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.thumb), true)));
    this.strip?.addEventListener('keydown', (e) => {
      if (!this.strip.isSameNode(e.target)) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); this.go(this.i + 1, true); }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); this.go(this.i - 1, true); }
    });
    this.strip?.addEventListener('scroll', debounce(() => this.onSwipe(), 60), { passive: true });
    this.querySelectorAll('[data-open]').forEach((b) => b.addEventListener('click', () => {
      this.go(this.slides.indexOf(b.closest('[data-slide]')), false);
      this.openBox(b);
    }));
    if ('IntersectionObserver' in window) {
      // A thin band a third of the way down the screen decides which photograph is current.
      this.seen = new IntersectionObserver((entries) => {
        if (this.narrow.matches || this.jumping) return;
        const hit = entries.filter((e) => e.isIntersecting).map((e) => this.slides.indexOf(e.target));
        if (hit.length) this.go(Math.min(...hit), false);
      }, { rootMargin: '-33% 0px -62% 0px' });
      this.slides.forEach((s) => this.seen.observe(s));
    }
    if (this.box) this.setupBox();
    this.go(0, false);
  }

  disconnectedCallback() {
    this.seen?.disconnect();
  }

  go(n, scroll) {
    const count = this.slides.length;
    this.i = (n + count) % count;
    this.slides.forEach((s, k) => s.classList.toggle('is-current', k === this.i));
    this.thumbs.forEach((t, k) => (k === this.i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
    this.indexEls.forEach((el) => (el.textContent = String(this.i + 1)));
    this.progress?.style.setProperty('--i', String(this.i));
    const cur = this.thumbs[this.i];
    const row = cur?.closest('.pdp__rail-wrap');
    if (this.narrow.matches && row && row.scrollWidth > row.clientWidth) {
      // Keep the current thumbnail in view in the phone row, without moving the page.
      const left = row.scrollLeft + cur.getBoundingClientRect().left - row.getBoundingClientRect().left - row.clientWidth / 2 + cur.offsetWidth / 2;
      row.scrollTo({ left, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    if (!scroll) return;
    const behavior = reduceMotion.matches ? 'auto' : 'smooth';
    if (this.narrow.matches) {
      this.strip.scrollTo({ left: this.slides[this.i].offsetLeft - this.strip.offsetLeft, behavior });
    } else {
      // Hold the observer while the page travels, so the rail does not flicker through every photograph.
      this.jumping = true;
      clearTimeout(this.jumpTimer);
      this.jumpTimer = setTimeout(() => (this.jumping = false), reduceMotion.matches ? 50 : 700);
      this.slides[this.i].scrollIntoView({ block: 'start', behavior });
    }
  }

  onSwipe() {
    if (!this.narrow.matches) return;
    const best = Math.round(this.strip.scrollLeft / Math.max(1, this.strip.clientWidth));
    if (best !== this.i && this.slides[best]) this.go(best, false);
  }

  setupBox() {
    const b = this.box;
    this.lb = {
      img: b.querySelector('[data-lb-img]'),
      view: b.querySelector('[data-lb-view]'),
      index: b.querySelector('[data-lb-index]'),
      hint: b.querySelector('[data-lb-hint]'),
      thumbs: b.querySelector('[data-lb-thumbs]'),
    };
    this.thumbs.forEach((t, k) => {
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('aria-label', t.getAttribute('aria-label'));
      btn.appendChild(t.querySelector('img').cloneNode());
      btn.addEventListener('click', () => this.show(k));
      li.appendChild(btn);
      this.lb.thumbs.appendChild(li);
    });
    b.querySelector('[data-lb-close]').addEventListener('click', () => b.close());
    b.querySelector('[data-lb-prev]').addEventListener('click', () => this.show(this.i - 1));
    b.querySelector('[data-lb-next]').addEventListener('click', () => this.show(this.i + 1));
    b.addEventListener('close', () => {
      document.body.classList.remove('is-locked');
      this.opener?.focus();
    });
    b.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.show(this.i + 1);
      if (e.key === 'ArrowLeft') this.show(this.i - 1);
      if (e.key !== 'Tab') return;
      // The browser lets Tab leave a modal dialog for its own toolbar; keep it inside.
      const items = [...b.querySelectorAll('button')].filter((el) => el.offsetParent !== null);
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    // Zoom: double click or double tap toggles the full file at native size.
    this.lb.img.addEventListener('dblclick', (e) => this.zoom(e));
    let lastTap = 0;
    this.lb.img.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastTap < 300) { e.preventDefault(); this.zoom(e.changedTouches[0]); }
      lastTap = now;
    });
    // Mouse drag pans a zoomed plate; touch pans by native scroll.
    let drag = null;
    this.lb.view.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || !this.lb.view.classList.contains('is-zoomed')) return;
      drag = { x: e.clientX, y: e.clientY, l: this.lb.view.scrollLeft, t: this.lb.view.scrollTop };
      this.lb.view.classList.add('is-dragging');
      this.lb.view.setPointerCapture(e.pointerId);
    });
    this.lb.view.addEventListener('pointermove', (e) => {
      if (!drag) return;
      this.lb.view.scrollLeft = drag.l - (e.clientX - drag.x);
      this.lb.view.scrollTop = drag.t - (e.clientY - drag.y);
    });
    const end = () => { drag = null; this.lb.view.classList.remove('is-dragging'); };
    this.lb.view.addEventListener('pointerup', end);
    this.lb.view.addEventListener('pointercancel', end);
  }

  openBox(opener) {
    if (!this.box || typeof this.box.showModal !== 'function') return;
    this.opener = opener;
    document.body.classList.add('is-locked');
    this.box.showModal();
    this.show(this.i);
    if (this.lb.hint) {
      this.lb.hint.hidden = false;
      this.lb.hint.style.opacity = '1';
      clearTimeout(this.hintTimer);
      this.hintTimer = setTimeout(() => {
        this.lb.hint.style.opacity = '0';
        setTimeout(() => (this.lb.hint.hidden = true), 400);
      }, 3000);
    }
  }

  show(n) {
    this.go(n, false);
    const slide = this.slides[this.i];
    const { img, view, index, thumbs } = this.lb;
    view.classList.remove('is-zoomed');
    img.src = slide.dataset.full;
    img.alt = slide.dataset.alt || '';
    if (index) index.textContent = String(this.i + 1);
    [...thumbs.querySelectorAll('button')].forEach((t, k) => (k === this.i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current')));
  }

  zoom(point) {
    const { view, img } = this.lb;
    const zoomed = view.classList.toggle('is-zoomed');
    if (!zoomed) return;
    const r = img.getBoundingClientRect();
    const fx = (point.clientX - r.left) / r.width;
    const fy = (point.clientY - r.top) / r.height;
    requestAnimationFrame(() => {
      view.scrollLeft = img.offsetWidth * fx - view.clientWidth / 2;
      view.scrollTop = img.offsetHeight * fy - view.clientHeight / 2;
    });
  }
}
customElements.define('flm-gallery', FlmGallery);

/* Product page: gallery position on phones, quantity, variant, buy bar. */
function initProduct() {
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty]');
    if (!step) return;
    const input = step.parentElement.querySelector('input');
    const next = Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.qty));
    input.value = String(next);
  });

  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const form = select.closest('form');
      const option = select.selectedOptions[0];
      form.querySelector('[data-variant-id]').value = select.value;
      const section = form.closest('.pdp');
      section?.querySelectorAll('[data-price]').forEach((el) => (el.textContent = option.dataset.price));
    });
  });

  /* Sticky bar: shown once the main Add to bag has scrolled above the screen,
     hidden again when the footer arrives so it never covers it. A scroll
     check rather than an observer, so it is exact on every frame. */
  const bar = document.querySelector('[data-buy-bar]');
  const add = document.querySelector('[data-main-add]');
  const footer = document.querySelector('.site-footer');
  if (bar && add) {
    const barButton = bar.querySelector('button');
    let queued = false;
    const update = () => {
      queued = false;
      const past = add.getBoundingClientRect().bottom < 0;
      const atFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
      const show = past && !atFooter;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      barButton.tabIndex = show ? 0 : -1;
    };
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
}

/* Pick a tile (home, scene 05): a list of toggle buttons, one frame each.
   Up and down arrows move between tiles, as in a list. Without script every
   frame shows in a column, so nothing depends on this. */
function initPick(root = document) {
  root.querySelectorAll('[data-pick]').forEach((pick) => {
    const tabs = [...pick.querySelectorAll('[data-pick-tab]')];
    const frames = [...pick.querySelectorAll('[data-pick-frame]')];
    const show = (i) => {
      tabs.forEach((t, n) => t.setAttribute('aria-pressed', String(n === i)));
      frames.forEach((f, n) => f.classList.toggle('is-current', n === i));
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => show(i));
      tab.addEventListener('keydown', (e) => {
        const d = { ArrowDown: 1, ArrowUp: -1 }[e.key];
        if (!d) return;
        e.preventDefault();
        const next = (i + d + tabs.length) % tabs.length;
        tabs[next].focus();
        show(next);
      });
    });
  });
}

/* Forms (sign-up, contact): the browser's own checks are kept for the no-script
   case; with script, each field says in words what is wrong, under itself, and
   the first wrong field takes focus. Offline is caught before the page posts.
   After a post, Shopify returns to the page with the thank-you or the error in
   place; that message takes focus so it is read out and seen. */
function initForms(root = document) {
  const words = window.flmStrings || {};
  root.querySelectorAll('form[data-validate]').forEach((form) => {
    form.noValidate = true;
    const fields = [...form.querySelectorAll('input[required], textarea[required], input[type="email"]')];
    const errorFor = (field) => form.querySelector(`#${field.id}-error`);
    const check = (field) => {
      const v = field.validity;
      let msg = '';
      if (v.valueMissing) msg = words.required || 'Please fill this in.';
      else if (v.typeMismatch || (field.type === 'email' && field.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(field.value.trim()))) msg = words.email || 'Please enter a full email address.';
      const out = errorFor(field);
      if (out) out.textContent = msg;
      if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      return !msg;
    };
    fields.forEach((field) => {
      field.addEventListener('blur', () => { if (field.value) check(field); });
      field.addEventListener('input', () => { if (field.getAttribute('aria-invalid') === 'true') check(field); });
    });
    form.addEventListener('submit', (e) => {
      const general = form.querySelector('[data-form-error]');
      if (general) general.textContent = '';
      const bad = fields.filter((f) => !check(f));
      if (bad.length) {
        e.preventDefault();
        bad[0].focus();
        return;
      }
      if (navigator.onLine === false) {
        e.preventDefault();
        const out = general || errorFor(fields[0]);
        if (out) out.textContent = words.offline || 'You seem to be offline. Check your connection and try again.';
        return;
      }
      const button = form.querySelector('[type="submit"]');
      if (button) { button.setAttribute('aria-busy', 'true'); button.dataset.label = button.innerHTML; button.textContent = words.sending || 'Sending'; }
    });
  });
  const status = root.querySelector('[data-form-status]');
  if (status) {
    status.scrollIntoView({ block: 'center' });
    status.focus({ preventScroll: true });
  }
}

/* Text and email sign-up: both fields on screen, one or both filled in. A
   number alone is enough only when Klaviyo is set on the form; otherwise it
   needs an email with it so Shopify can keep it. */
function usDigits(value) {
  let d = String(value || '').replace(/\D/g, '');
  if (d.length === 11 && d[0] === '1') d = d.slice(1);
  return d;
}
function formatUs(d) {
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 10)}`;
}
function initTexts(root = document) {
  const words = window.flmStrings || {};
  const remember = 'flm-texts';
  root.querySelectorAll('form[data-texts]').forEach((form) => {
    const phone = form.querySelector('#TextsPhone');
    const email = form.querySelector('#TextsEmail');
    if (form.querySelector('[data-form-status]')) {
      try { sessionStorage.removeItem(remember); } catch (err) { /* nothing was stored */ }
    }
    if (!phone || !email) return;
    form.noValidate = true;
    const general = form.querySelector('[data-form-error]');
    const tag = form.querySelector('[data-texts-tag]');
    const key = form.dataset.klaviyoKey;
    const list = form.dataset.klaviyoList;
    // A number alone can only be kept by an SMS app; without one it needs an email with it.
    const smsReady = form.dataset.smsReady === 'true' && !!key && !!list;

    const say = (field, msg) => {
      const out = form.querySelector(`#${field.id}-error`);
      if (out) out.textContent = msg;
      if (msg) field.setAttribute('aria-invalid', 'true'); else field.removeAttribute('aria-invalid');
      return !msg;
    };
    const checkPhone = () => {
      const d = usDigits(phone.value);
      if (!d) return say(phone, '');
      if (d.length !== 10 || /^[01]/.test(d)) return say(phone, words.phoneInvalid || 'Please enter a 10 digit US mobile number.');
      return say(phone, '');
    };
    const checkEmail = () => {
      const v = email.value.trim();
      if (!v) return say(email, '');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return say(email, words.email || 'Please enter a full email address.');
      return say(email, '');
    };

    // Shopify sent the form back with an error: put the number back.
    if (form.querySelector('#TextsEmail[aria-invalid]')) {
      let saved = '';
      try { saved = sessionStorage.getItem(remember) || ''; } catch (err) { saved = ''; }
      if (saved) phone.value = formatUs(usDigits(saved));
    }

    phone.addEventListener('input', () => {
      const d = usDigits(phone.value);
      const end = phone.selectionStart === phone.value.length;
      phone.value = formatUs(d.slice(0, 10));
      if (end) phone.setSelectionRange(phone.value.length, phone.value.length);
      if (phone.getAttribute('aria-invalid') === 'true') checkPhone();
    });
    phone.addEventListener('blur', () => { if (phone.value) checkPhone(); });
    email.addEventListener('input', () => { if (email.getAttribute('aria-invalid') === 'true') checkEmail(); });
    email.addEventListener('blur', () => { if (email.value) checkEmail(); });

    const thankInPlace = () => {
      const ok = document.createElement('div');
      ok.className = 'form-status form-status--ok texts__thanks';
      ok.setAttribute('role', 'status');
      ok.tabIndex = -1;
      const p = document.createElement('p');
      p.textContent = form.dataset.success || '';
      ok.append(p);
      form.replaceChildren(ok);
      ok.focus({ preventScroll: true });
      try { sessionStorage.removeItem(remember); } catch (err) { /* nothing was stored */ }
    };
    const klaviyo = (attributes) => fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
      body: JSON.stringify({ data: {
        type: 'subscription',
        attributes: { custom_source: form.dataset.source || 'Text sign-up page', profile: { data: { type: 'profile', attributes } } },
        relationships: { list: { data: { type: 'list', id: list } } },
      } }),
    }).then((res) => { if (!res.ok) throw new Error(`Klaviyo ${res.status}`); });

    let sending = false;
    form.addEventListener('submit', async (e) => {
      if (general) general.textContent = '';
      const hasPhone = !!usDigits(phone.value);
      const hasEmail = !!email.value.trim();
      const stop = (field, msg) => { e.preventDefault(); if (msg !== undefined) say(field, msg); field.focus(); };
      if (!hasPhone && !hasEmail) {
        e.preventDefault();
        if (general) general.textContent = words.needOne || 'Please enter a mobile number or an email address.';
        phone.focus();
        return;
      }
      if (hasPhone && !checkPhone()) { stop(phone); return; }
      if (hasEmail && !checkEmail()) { stop(email); return; }
      if (hasPhone && !hasEmail && !smsReady) { stop(email, words.needEmail || 'Please add your email too, so we can keep your number.'); return; }
      if (navigator.onLine === false) {
        e.preventDefault();
        if (general) general.textContent = words.offline || 'You seem to be offline. Check your connection and try again.';
        return;
      }
      const e164 = hasPhone ? `+1${usDigits(phone.value)}` : '';
      try { sessionStorage.setItem(remember, e164); } catch (err) { /* private mode: the number is asked again */ }
      if (tag) tag.value = hasPhone ? (form.dataset.tagSms || 'sms-signup') : (form.dataset.tagEmail || 'email-signup');
      phone.value = e164;
      phone.disabled = !hasPhone; // an empty number is not sent
      const button = form.querySelector('[type="submit"]');
      const busy = () => {
        if (!button) return;
        button.setAttribute('aria-busy', 'true');
        button.dataset.label = button.innerHTML;
        button.textContent = words.sending || 'Sending';
      };
      const undo = () => {
        phone.disabled = false;
        phone.value = formatUs(usDigits(e164));
        if (button && button.dataset.label) { button.removeAttribute('aria-busy'); button.innerHTML = button.dataset.label; }
      };

      if (!smsReady) { busy(); return; } // the Shopify form sends itself
      e.preventDefault();
      if (sending) return;
      sending = true;
      busy();
      const subscriptions = {};
      const attributes = { subscriptions };
      if (hasPhone) { attributes.phone_number = e164; subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } }; }
      if (hasEmail) { attributes.email = email.value.trim(); subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } }; }
      try {
        await klaviyo(attributes);
        if (hasEmail) form.submit(); else thankInPlace();
      } catch (err) {
        console.error(err);
        sending = false;
        undo();
        if (general) general.textContent = words.notSaved || 'We could not save that just now. Please try again.';
      }
    });
  });
}

/* A page restored from the back-forward cache keeps a pressed Send button; put its label back. */
window.addEventListener('pageshow', () => {
  document.querySelectorAll('form[data-validate] [aria-busy="true"]').forEach((b) => {
    b.removeAttribute('aria-busy');
    if (b.dataset.label) b.innerHTML = b.dataset.label;
  });
});

function init() {
  // An image that finished before the head listener existed (bfcache, a fast cache hit) still shows.
  document.querySelectorAll('img[loading="lazy"]').forEach((img) => { if (img.complete) img.classList.add('is-loaded'); });
  initProduct();
  initPick();
  initForms();
  initTexts();
}
document.addEventListener('shopify:section:load', (e) => { initPick(e.target); initForms(e.target); initTexts(e.target); });
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
